import { circleVsAabb, circleVsCircle, segmentVsAabbs } from '../engine/collision.js';
import { ENEMY_STATS } from '../data/enemies.data.js';
import { Drone } from './enemies/drone.js';
import { Enforcer } from './enemies/enforcer.js';
import { Zealot } from './enemies/zealot.js';
import { Minmon } from './enemies/minmon.js';
import { Zilua } from './enemies/zilua.js';

export function radiusForEnemyType(type) {
  return ENEMY_STATS[type]?.radius ?? 0.6;
}

export function isSpawnPointClear(game, x, z, radius, { minPlayerDistance = 0, ignoreEnemy = null } = {}) {
  if (typeof game.room?.isWalkableSpawn === 'function' && !game.room.isWalkableSpawn(x, z, radius)) {
    return false;
  }

  for (const wall of game.room.walls) {
    if (circleVsAabb(x, z, radius, wall.minX, wall.minZ, wall.maxX, wall.maxZ)) return false;
  }

  const player = game.player;
  if (player?.alive && minPlayerDistance > 0) {
    const d = Math.hypot(player.x - x, player.z - z);
    const requiredDistance = player.radius + radius + minPlayerDistance;
    if (d < requiredDistance) return false;
  }

  for (const e of game.enemies) {
    if (!e?.alive || e === ignoreEnemy) continue;
    if (circleVsCircle(x, z, radius, e.x, e.z, e.radius + 0.06)) return false;
  }

  return true;
}

export function setSandboxDebugPanelVisible(game, show) {
  if (!game._debugPanelEl) return;
  game._debugPanelEl.classList.toggle('hidden', !show);
}

export function addEnemyByType(game, type, x, z) {
  let enemy = null;
  if (type === 'drone') {
    enemy = new Drone({ scene: game.scene, x, z, audio: game.audio });
  }
  if (type === 'enforcer') {
    enemy = new Enforcer({ scene: game.scene, x, z, audio: game.audio });
  }
  if (type === 'zealot') {
    enemy = new Zealot({ scene: game.scene, x, z, audio: game.audio });
  }
  if (type === 'minmon') {
    enemy = new Minmon({ scene: game.scene, x, z, audio: game.audio });
  }
  if (type === 'zilua') {
    enemy = new Zilua({ scene: game.scene, x, z, audio: game.audio });
  }
  if (!enemy) return null;
  orientEnemyOnSpawn(game, enemy);
  game.enemies.push(enemy);
  return enemy;
}

export function orientEnemyOnSpawn(game, enemy) {
  if (!enemy?.group) return;

  // Spawned enemies default to +Z forward which can make some seeded layouts
  // look like enemies are staring directly into walls. Choose a direction that
  // keeps visual intent (toward player/center) but avoids near-wall headings.
  const playerAlive = !!game.player?.alive;
  const targetX = playerAlive ? game.player.x : 0;
  const targetZ = playerAlive ? game.player.z : 0;
  let dx = targetX - enemy.x;
  let dz = targetZ - enemy.z;
  let len = Math.hypot(dx, dz);

  if (len < 0.001) {
    dx = -enemy.x;
    dz = -enemy.z;
    len = Math.hypot(dx, dz);
  }

  if (len < 0.001) return;

  const preferredX = dx / len;
  const preferredZ = dz / len;
  let bestX = preferredX;
  let bestZ = preferredZ;

  const walls = game.room?.walls || [];
  if (walls.length > 0) {
    const rayLen = 7;
    const enemyRadius = Math.max(0.35, enemy.radius || game._radiusForEnemyType(enemy.type) || 0.6);
    const startOffset = enemyRadius + 0.2;
    const minForwardClearanceT = 0.22;
    let bestScore = -Infinity;
    let bestFallbackScore = -Infinity;
    let fallbackX = preferredX;
    let fallbackZ = preferredZ;
    const sampleCount = 16;
    for (let i = 0; i < sampleCount; i++) {
      const angle = (i / sampleCount) * Math.PI * 2;
      const cx = Math.sin(angle);
      const cz = Math.cos(angle);
      const startX = enemy.x + cx * startOffset;
      const startZ = enemy.z + cz * startOffset;
      const endX = enemy.x + cx * rayLen;
      const endZ = enemy.z + cz * rayLen;
      const t = segmentVsAabbs(startX, startZ, endX, endZ, walls);
      const clearance = t === null ? 1 : Math.max(0, Math.min(1, t));
      const align = cx * preferredX + cz * preferredZ;
      const walkableAhead = game.room?.isWalkableSpawn?.(enemy.x + cx * 1.1, enemy.z + cz * 1.1, enemyRadius * 0.55) ?? true;
      const score = clearance * 2.4 + align * 0.35;

      // Keep a general fallback even for constrained spawns.
      if (score > bestFallbackScore) {
        bestFallbackScore = score;
        fallbackX = cx;
        fallbackZ = cz;
      }

      // Reject headings that immediately face blocked/non-walkable space.
      if (!walkableAhead || clearance < minForwardClearanceT) continue;

      if (score > bestScore) {
        bestScore = score;
        bestX = cx;
        bestZ = cz;
      }
    }

    if (!Number.isFinite(bestScore) || bestScore === -Infinity) {
      bestX = fallbackX;
      bestZ = fallbackZ;
    }
  }

  enemy.aimX = bestX;
  enemy.aimZ = bestZ;
  enemy.faceAim?.();
}

export function findSafeSummonSpawn(game, type, originX, originZ, opts = {}) {
  if (!game.room) return null;
  const {
    attempts = 14,
    minRadius = 1.1,
    maxRadius = 3,
    minPlayerDistance = 3,
    ignoreEnemy = null,
  } = opts;
  const radius = game._radiusForEnemyType(type);

  for (let i = 0; i < attempts; i++) {
    const angle = game.rng() * Math.PI * 2;
    const dist = minRadius + game.rng() * Math.max(0.1, maxRadius - minRadius);
    const x = originX + Math.cos(angle) * dist;
    const z = originZ + Math.sin(angle) * dist;
    if (game._isSpawnPointClear(x, z, radius, { minPlayerDistance, ignoreEnemy })) {
      return { x, z };
    }
  }

  const anchors = game.room.enemySpawns || [];
  for (const anchor of anchors) {
    if (game._isSpawnPointClear(anchor.x, anchor.z, radius, { minPlayerDistance, ignoreEnemy })) {
      return { x: anchor.x, z: anchor.z };
    }
  }

  return null;
}


